"use client";

import { useT } from "@cms/module-api/client";
import { createContext, createElement, useContext, useCallback, useRef, type ReactNode } from "react";
import type { StorefrontFeatures } from "../features";
import { translateStorefront } from "./storefront-i18n";

export type WebshopTranslate = (key: string, fallback: string) => string;
const Labels = createContext<StorefrontFeatures["labels"]>({});
export function StorefrontLabelsProvider({ labels, children }: { labels: StorefrontFeatures["labels"]; children: ReactNode }) {
  return createElement(Labels.Provider, { value: labels }, children);
}

/**
 * Uses the explicit CMS route/block locale on the public site. Admin screens
 * omit it and keep using the CMS user-interface locale supplied by useT().
 */
export function useWebshopT(locale?: string, overrides?: StorefrontFeatures["labels"]): WebshopTranslate {
  const inherited = useContext(Labels);
  const labelsRef = useRef(overrides ?? inherited);
  labelsRef.current = overrides ?? inherited;
  const translate = useT();
  const translateRef = useRef(translate);
  translateRef.current = translate;
  return useCallback(
    (key: string, fallback: string) => {
      const override = locale ? (labelsRef.current[locale] ?? labelsRef.current[locale.split("-")[0]])?.[key] : undefined;
      if (override) return override;
      if (locale) return translateStorefront(locale, key, fallback);
      const value = translateRef.current(key);
      return value && value !== key ? value : fallback;
    },
    [locale],
  );
}
